import React, { useState } from 'react';
import { useTheme } from '../App';
import { X, Check } from 'lucide-react';
export interface KPICategory {
  name: string;
  kpis: KPIItem[];
}
export interface KPIItem {
  id: string;
  title: string;
  selected: boolean;
}
export interface Role {
  id: string;
  name: string;
  description: string;
  categories: string[];
}
interface KPICustomizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (selectedKPIs: Record<string, KPIItem[]>) => void;
  initialSelectedKPIs: Record<string, KPIItem[]>;
}
const KPICustomizeModal: React.FC<KPICustomizeModalProps> = ({
  isOpen,
  onClose,
  onApply,
  initialSelectedKPIs
}) => {
  const {
    isDarkMode
  } = useTheme();
  const [selectedRole, setSelectedRole] = useState<string>('general');
  const [selectedKPIs, setSelectedKPIs] = useState<Record<string, KPIItem[]>>(initialSelectedKPIs);
  // Sample roles data
  const roles: Role[] = [{
    id: 'general',
    name: 'General Manager',
    description: 'Overall business performance and operations',
    categories: ['management', 'sales', 'inventory', 'compliance']
  }, {
    id: 'inventory',
    name: 'Inventory Manager',
    description: 'Inventory management and product tracking',
    categories: ['inventory', 'compliance']
  }, {
    id: 'sales',
    name: 'Sales Manager',
    description: 'Sales performance and customer metrics',
    categories: ['sales', 'marketing', 'overview']
  }, {
    id: 'marketing',
    name: 'Marketing Director',
    description: 'Customer acquisition and retention',
    categories: ['marketing', 'ecommerce']
  }, {
    id: 'compliance',
    name: 'Compliance Officer',
    description: 'Regulatory compliance and audit readiness',
    categories: ['compliance', 'inventory']
  }, {
    id: 'ecommerce',
    name: 'E-Commerce',
    description: 'Online sales operations and digital customer experience',
    categories: ['ecommerce', 'marketing', 'sales']
  }, {
    id: 'custom',
    name: 'Custom',
    description: 'Customize your own metrics selection',
    categories: ['management', 'marketing', 'sales', 'overview', 'compliance', 'inventory', 'ecommerce']
  }];
  // Handle role selection
  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);
    // If not custom, apply role's default KPI selection
    if (roleId !== 'custom') {
      const role = roles.find(r => r.id === roleId);
      if (role) {
        const newSelectedKPIs = {
          ...selectedKPIs
        };
        // Update KPI selection based on role
        Object.keys(newSelectedKPIs).forEach(category => {
          const isCategoryIncluded = role.categories.includes(category);
          newSelectedKPIs[category] = newSelectedKPIs[category].map(kpi => ({
            ...kpi,
            selected: isCategoryIncluded
          }));
        });
        setSelectedKPIs(newSelectedKPIs);
      }
    }
  };
  // Handle KPI selection
  const handleKPIToggle = (categoryName: string, kpiId: string) => {
    setSelectedKPIs(prev => {
      const updatedCategory = prev[categoryName].map(kpi => kpi.id === kpiId ? {
        ...kpi,
        selected: !kpi.selected
      } : kpi);
      return {
        ...prev,
        [categoryName]: updatedCategory
      };
    });
    // If we're making custom selections, switch to custom role
    setSelectedRole('custom');
  };
  // Handle select all for a category
  const handleSelectAll = (categoryName: string) => {
    setSelectedKPIs(prev => {
      const updatedCategory = prev[categoryName].map(kpi => ({
        ...kpi,
        selected: true
      }));
      return {
        ...prev,
        [categoryName]: updatedCategory
      };
    });
    setSelectedRole('custom');
  };
  // Handle select none for a category
  const handleSelectNone = (categoryName: string) => {
    setSelectedKPIs(prev => {
      const updatedCategory = prev[categoryName].map(kpi => ({
        ...kpi,
        selected: false
      }));
      return {
        ...prev,
        [categoryName]: updatedCategory
      };
    });
    setSelectedRole('custom');
  };
  // Count selected KPIs in a category
  const getSelectedCount = (categoryName: string) => {
    return selectedKPIs[categoryName]?.filter(kpi => kpi.selected).length || 0;
  };
  // Total count of KPIs in a category
  const getTotalCount = (categoryName: string) => {
    return selectedKPIs[categoryName]?.length || 0;
  };
  // Handle apply changes
  const handleApply = () => {
    onApply(selectedKPIs);
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" data-id="element-206">
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-auto rounded-lg shadow-lg ${isDarkMode ? 'bg-[#222930] text-white' : 'bg-white text-black'}`} data-id="element-207">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-700 bg-inherit" data-id="element-208">
          <div data-id="element-209">
            <h2 className="text-xl font-bold" data-id="element-210">Customize My Metrics</h2>
            <p className="text-sm text-gray-400" data-id="element-211">
              Select your role and choose which metrics to display
            </p>
          </div>
          <button className="p-1 rounded-full hover:bg-gray-700" onClick={onClose} data-id="element-212">
            <X size={24} data-id="element-213" />
          </button>
        </div>
        <div className="p-4" data-id="element-214">
          {/* Role Selector */}
          <div className="mb-6" data-id="element-215">
            <label className="block font-medium mb-1" data-id="element-216">Select Your Role</label>
            <select value={selectedRole} onChange={e => handleRoleSelect(e.target.value)} className={`w-full p-3 rounded-md ${isDarkMode ? 'bg-[#161c22] border-gray-700 text-white' : 'bg-white border-gray-300 text-black'} border`} data-id="element-217">
              {roles.map(role => <option key={role.id} value={role.id} data-id="element-218">
                  {role.name}
                </option>)}
            </select>
            <p className="text-sm text-gray-400 mt-1" data-id="element-219">
              {roles.find(r => r.id === selectedRole)?.description}
            </p>
          </div>
          {/* KPI Categories */}
          <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-2" data-id="element-220">
            {Object.entries(selectedKPIs).map(([category, kpis]) => <div key={category} className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#161c22]' : 'bg-gray-50'}`} data-id="element-221">
                <div className="flex justify-between items-center mb-3" data-id="element-222">
                  <h3 className="font-semibold capitalize" data-id="element-223">
                    {category} ({getSelectedCount(category)}/
                    {getTotalCount(category)})
                  </h3>
                  <div className="flex space-x-2 text-sm" data-id="element-224">
                    <button className="text-blue-400 hover:underline" onClick={() => handleSelectAll(category)} data-id="element-225">
                      All
                    </button>
                    <button className="text-blue-400 hover:underline" onClick={() => handleSelectNone(category)} data-id="element-226">
                      None
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-227">
                  {kpis.map(kpi => <label key={kpi.id} className="flex items-center space-x-2 cursor-pointer" data-id="element-228">
                      <input type="checkbox" checked={kpi.selected} onChange={() => handleKPIToggle(category, kpi.id)} className="rounded" data-id="element-229" />
                      <span data-id="element-230">{kpi.title}</span>
                    </label>)}
                </div>
              </div>)}
          </div>
          {/* Footer Actions */}
          <div className="flex justify-end space-x-3 mt-6" data-id="element-231">
            <button className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} onClick={onClose} data-id="element-232">
              Cancel
            </button>
            <button className="px-4 py-2 bg-[#2196f3] text-white rounded-md hover:bg-[#1976d2] flex items-center" onClick={handleApply} data-id="element-233">
              <Check size={18} className="mr-2" data-id="element-234" />
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default KPICustomizeModal;